'use client';

import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { cn } from '@/app/utils';
import { handleServiceError } from '@/app/utils/client';
import { getLiveEmergencyContact, submitLiveEmergencyContact } from '../apis';
import {
  EMPTY_EMERGENCY_CONTACT,
  type EmergencyContactError,
  type EmergencyContactForm,
  normalizeEmergencyContact,
  validateEmergencyContact,
} from './EmergencyContactInput.utils';
import { UserInfoDialog } from './UserInfoDialog';

const EmergencyContactInput = () => {
  const t = useTranslations();
  const [isEdit, setIsEdit] = useState(false);
  const [emergencyContact, setEmergencyContact] = useState<EmergencyContactForm>(EMPTY_EMERGENCY_CONTACT);
  const [displayError, setDisplayError] = useState<EmergencyContactError | null>(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const editSnapshotRef = useRef<EmergencyContactForm>(EMPTY_EMERGENCY_CONTACT);

  const { loading: loadingContact } = useFetch(getLiveEmergencyContact, {
    onSuccess: ({ data, error }) => {
      if (error) {
        handleServiceError(error, t);
        return;
      }
      if (data) {
        setEmergencyContact(
          normalizeEmergencyContact({
            name: data.name ?? '',
            phone: data.phone ?? '',
            relationship: data.relationship ?? '',
          }),
        );
      }
    },
    onError: () => {
      toast({ type: 'error', message: t('personal.emergencyContact.loadError') });
    },
  });

  const { run: updateEmergencyContact, loading: submitting } = useFetch(
    () => submitLiveEmergencyContact(normalizeEmergencyContact(emergencyContact)),
    {
      manual: true,
      onSuccess: ({ error }) => {
        if (error) {
          if (error.reason === 'LiveInvalidContactPhone') {
            setDisplayError('phoneInvalid');
            setOpen(false);
            toast({ type: 'error', message: t('personal.emergencyContact.errors.phoneInvalid') });
            return;
          }
          handleServiceError(error, t);
          return;
        }
        toast({ type: 'success', message: t('personal.emergencyContact.success') });
        setEmergencyContact(normalizeEmergencyContact(emergencyContact));
        setIsEdit(false);
        setExpanded(true);
        setOpen(false);
      },
      onError: () => {
        toast({ type: 'error', message: t('personal.emergencyContact.error') });
      },
    },
  );

  useEffect(() => {
    if (loadingContact) setDisplayError(null);
  }, [loadingContact]);

  const handleInputChange = (field: keyof EmergencyContactForm, value: string) => {
    const nextContact = { ...emergencyContact, [field]: value };
    setEmergencyContact(nextContact);
    setDisplayError(validateEmergencyContact(nextContact));
  };

  const handleEdit = () => {
    editSnapshotRef.current = emergencyContact;
    setIsEdit(true);
    setExpanded(true);
    setDisplayError(null);
  };

  const handleCancel = () => {
    setEmergencyContact(editSnapshotRef.current);
    setDisplayError(null);
    setIsEdit(false);
    setExpanded(false);
  };

  const handleSave = () => {
    const error = validateEmergencyContact(emergencyContact);
    setDisplayError(error);
    if (!error) setOpen(true);
  };

  const relationshipOptions = [
    { value: 'parent', label: t('personal.emergencyContact.relationshipParent') },
    { value: 'relative', label: t('personal.emergencyContact.relationshipRelative') },
    { value: 'friend', label: t('personal.emergencyContact.relationshipFriend') },
    { value: 'other', label: t('personal.emergencyContact.relationshipOther') },
  ];
  return (
    <>
      <div className="mb-4 flex items-center gap-3">
        <span className="min-w-0 flex-1 text-[18px]">{t('personal.emergencyContact.label')}</span>
        {expanded && (
          <div className="flex items-center gap-4 font-normal text-[16px] leading-8">
            {isEdit && (
              <button
                disabled={loadingContact || submitting}
                onClick={handleCancel}
                type="button"
                className="cursor-pointer text-system-white64 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('common.dialog.cancel')}
              </button>
            )}
            <button
              disabled={loadingContact || (Boolean(displayError) && isEdit) || submitting}
              onClick={isEdit ? handleSave : handleEdit}
              type="button"
              className={cn(
                'cursor-pointer',
                isEdit ? (displayError ? 'text-system-white64' : 'text-white') : 'text-system-blue02',
              )}
            >
              {isEdit ? t('personal.save') : t('personal.edit')}
            </button>
          </div>
        )}
        {!isEdit && (
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls="emergency-contact-details"
            onClick={() => setExpanded((value) => !value)}
            className="flex size-8 shrink-0 items-center justify-center text-system-white64"
          >
            <ChevronDown aria-hidden="true" className={cn('size-4 transition-transform', expanded && 'rotate-180')} />
          </button>
        )}
      </div>

      <div
        id="emergency-contact-details"
        aria-hidden={!expanded}
        inert={!expanded ? true : undefined}
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
          expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="mb-6">
            <Label className="mb-4 inline-block font-medium text-base lg:mb-3 lg:text-sm">
              {t('personal.emergencyContact.name')}
            </Label>
            <Input
              value={emergencyContact.name}
              onChange={(event) => handleInputChange('name', event.target.value)}
              placeholder={t('personal.emergencyContact.namePlaceholder')}
              maxLength={30}
              disabled={!isEdit || loadingContact}
            />
          </div>

          <div className="mb-6">
            <Label className="mb-4 inline-block font-medium text-base lg:mb-3 lg:text-sm">
              {t('personal.emergencyContact.phone')}
            </Label>
            <Input
              value={emergencyContact.phone}
              onChange={(event) => handleInputChange('phone', event.target.value)}
              placeholder={t('personal.emergencyContact.phonePlaceholder')}
              inputMode="numeric"
              maxLength={11}
              disabled={!isEdit || loadingContact}
            />
          </div>

          <div className="mb-6">
            <Label className="mb-4 inline-block font-medium text-base lg:mb-3 lg:text-sm">
              {t('personal.emergencyContact.relationship')}
            </Label>
            <Select
              value={emergencyContact.relationship}
              onValueChange={(value) => handleInputChange('relationship', value)}
              disabled={!isEdit || loadingContact}
            >
              <SelectTrigger className="h-10" disabled={!isEdit || loadingContact}>
                <SelectValue placeholder={t('personal.emergencyContact.relationshipPlaceholder')} />
              </SelectTrigger>
              <SelectContent className="w-[var(--radix-select-trigger-width)]">
                {relationshipOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-2 text-system-white48 text-xs">{t('personal.emergencyContact.description')}</div>
          </div>

          {isEdit && displayError && (
            <p className="mb-4 text-sm text-system-error">{t(`personal.emergencyContact.errors.${displayError}`)}</p>
          )}

          <UserInfoDialog
            title={t('personal.emergencyContact.confirmTitle')}
            description={t('personal.emergencyContact.confirmDescription')}
            open={open}
            onChange={updateEmergencyContact}
            setOpen={setOpen}
            loading={submitting}
            closeOnConfirm={false}
          />
        </div>
      </div>
    </>
  );
};

export default EmergencyContactInput;
